// src/components/discover/FeedItemDetailSheet.tsx
//
// Bottom sheet that opens when a user taps the body of a swipe card.
// Shows: full-bleed photo carousel, name + rating + price + open status,
// description, editorial summary (when from Google), reviews list
// (newest first), action buttons.
//
// Reviews are fetched on-demand via the existing entityDetail Cloud
// Function so we don't pay for review fetches on items the user
// never opens.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Chip,
  Button,
  Divider,
  Avatar,
  CircularProgress,
} from '@mui/material';
import {
  Close,
  Star,
  StarHalf,
  StarBorder,
  ChevronLeft,
  ChevronRight,
  OpenInNew,
  ChatBubbleOutline,
  PushPin,
  PushPinOutlined,
} from '@mui/icons-material';
import type { FeedItem } from '../../hooks/useFeed';
import { useEntityDetail } from '../../hooks/useEntityDetail';
import type { PlaceReview } from '../../hooks/useEntityDetail';
import { MiniMap } from '../map/MiniMap';
import { useIsPinned, usePinActions } from '../../hooks/usePinned';
import { useT } from '../../i18n/useT';

interface FeedItemDetailSheetProps {
  open: boolean;
  item: FeedItem | null;
  onClose: () => void;
  /** "Ask AI about this" — sends chat_prompt to the chat tab. */
  onAskAI: (item: FeedItem) => void;
}

export const FeedItemDetailSheet: React.FC<FeedItemDetailSheetProps> = ({
  open,
  item,
  onClose,
  onAskAI,
}) => {
  const t = useT();
  const [photoIdx, setPhotoIdx] = useState(0);

  // Reset carousel index when the item changes
  useEffect(() => {
    setPhotoIdx(0);
  }, [item?.id]);

  // Fire entityDetail fetch only when sheet opens for a place-backed
  // item. AI knowledge picks have no place_id so the fetch is skipped.
  const detailParams = useMemo(() => {
    if (!open || !item || !item.place_id) return null;
    return {
      name: item.title,
      category: 'place',
      location: item.location
        ? { lat: item.location.lat, lng: item.location.lng }
        : undefined,
    };
  }, [open, item]);

  const { detail, loading } = useEntityDetail(detailParams);
  const isPinned = useIsPinned(open && item ? item.id : null);
  const { pin, unpin } = usePinActions();

  if (!item) return null;

  // Photos to render in the sheet — prefer fetched detail (which can
  // have higher-res or more photos than the feed doc), else fall back
  // to whatever the feed doc carried.
  const photos =
    detail?.photos?.map((p) => p.url) ||
    item.photo_urls ||
    (item.image_url ? [item.image_url] : []);
  const currentPhoto = photos[Math.min(photoIdx, Math.max(0, photos.length - 1))];

  // Merge feed doc + fetched detail. Fetched detail wins where present
  // (more authoritative — fresher Place Details snapshot).
  const rating = detail?.rating ?? item.rating;
  const ratingsCount = detail?.user_ratings_total ?? item.user_ratings_total;
  const priceLevel = detail?.price_level ?? item.price_level;
  const openNow = detail?.open_now ?? item.is_open_now;
  const hours = detail?.hours;
  const editorial = detail?.editorial_summary ?? item.editorial_summary;
  const address = detail?.address ?? item.location.address;
  const reviews: PlaceReview[] = detail?.reviews || [];

  // Maps deep-link priority:
  //   1. entityDetail's action_urls.maps_open (best — uses place_id when known)
  //   2. Build from place_id directly if we have it but detail hasn't loaded yet
  //   3. Build from name + coordinates (works for knowledge picks)
  //   4. Build from address + coordinates (last resort)
  // All paths produce a Google Maps search URL that opens the native
  // app on mobile when installed. We URL-encode the query carefully
  // so non-Latin titles (e.g. Café "Le Procope") work cleanly.
  const mapsUrl: string | null = (() => {
    if (detail?.action_urls?.maps_open) return detail.action_urls.maps_open;
    if (item.place_id) {
      // Place-id-based search guarantees the right pin even when names
      // collide (e.g. multiple cafés with the same name in a city).
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        item.title
      )}&query_place_id=${encodeURIComponent(item.place_id)}`;
    }
    const hasCoords =
      typeof item.location?.lat === 'number' &&
      typeof item.location?.lng === 'number' &&
      !(item.location.lat === 0 && item.location.lng === 0);
    if (item.title && hasCoords) {
      // Name + coords is the most reliable for knowledge picks: the
      // name pinpoints the venue, the coords prevent Google from
      // mismatching with a same-name place in another city.
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        item.title
      )}&center=${item.location.lat},${item.location.lng}`;
    }
    if (address && hasCoords) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        address
      )}`;
    }
    if (hasCoords) {
      return `https://www.google.com/maps/search/?api=1&query=${item.location.lat},${item.location.lng}`;
    }
    return null;
  })();

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: '92vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Drag handle */}
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1, pb: 0.5 }}>
        <Box
          sx={{
            width: 40,
            height: 4,
            borderRadius: 2,
            bgcolor: 'action.disabledBackground',
          }}
        />
      </Box>

      {/* Close button */}
      <IconButton
        onClick={onClose}
        size="small"
        aria-label={t("Close")}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          bgcolor: 'rgba(255,255,255,0.85)',
          zIndex: 2,
          '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
        }}
      >
        <Close fontSize="small" />
      </IconButton>

      {/* Scrollable body */}
      <Box sx={{ overflowY: 'auto', flex: 1 }}>
        {/* Photo carousel */}
        {currentPhoto ? (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: '16/10',
              backgroundImage: `url(${currentPhoto})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              bgcolor: 'action.hover',
              userSelect: 'none',
            }}
          >
            {photos.length > 1 && (
              <>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 12,
                    left: 0,
                    right: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 0.5,
                    px: 2,
                  }}
                >
                  {photos.map((_, i) => (
                    <Box
                      key={i}
                      sx={{
                        flex: '1 1 0',
                        maxWidth: 50,
                        height: 3,
                        borderRadius: 2,
                        bgcolor:
                          i === photoIdx
                            ? 'rgba(255,255,255,0.95)'
                            : 'rgba(255,255,255,0.4)',
                        transition: 'background-color 0.2s ease',
                        boxShadow: '0 0 2px rgba(0,0,0,0.4)',
                      }}
                    />
                  ))}
                </Box>
                <IconButton
                  onClick={() =>
                    setPhotoIdx((i) => (i - 1 + photos.length) % photos.length)
                  }
                  sx={{
                    position: 'absolute',
                    left: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(0,0,0,0.4)',
                    color: 'common.white',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                  }}
                  aria-label={t("Previous photo")}
                >
                  <ChevronLeft />
                </IconButton>
                <IconButton
                  onClick={() => setPhotoIdx((i) => (i + 1) % photos.length)}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(0,0,0,0.4)',
                    color: 'common.white',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                  }}
                  aria-label={t("Next photo")}
                >
                  <ChevronRight />
                </IconButton>
              </>
            )}
          </Box>
        ) : item.origin === 'llm-knowledge' ? (
          <Box
            sx={{
              width: '100%',
              aspectRatio: '16/10',
              background: 'linear-gradient(135deg, #fef3c7 0%, #fcd34d 100%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography sx={{ fontSize: 64, opacity: 0.7 }}>🗒️</Typography>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: 'rgba(120,53,15,0.85)',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              
              {t("Local pick")}
            </Typography>
          </Box>
        ) : null}

        {/* Body */}
        <Box sx={{ p: 2.5 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            {item.title}
          </Typography>

          {/* Rating + price + open-now */}
          {(rating !== undefined ||
            priceLevel !== undefined ||
            openNow !== undefined) && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
                mb: 1.5,
              }}
            >
              {rating !== undefined && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <StarsRow rating={rating} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {rating.toFixed(1)}
                  </Typography>
                  {ratingsCount !== undefined && (
                    <Typography variant="body2" color="text.secondary">
                      ({formatRatingsCount(ratingsCount)}  {t("reviews)")}
                    </Typography>
                  )}
                </Box>
              )}
              {priceLevel !== undefined && priceLevel > 0 && (
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, letterSpacing: 1 }}
                >
                  {'$'.repeat(Math.min(4, priceLevel))}
                  <span style={{ opacity: 0.3 }}>
                    {'$'.repeat(Math.max(0, 4 - priceLevel))}
                  </span>
                </Typography>
              )}
              {openNow !== undefined && (
                <Chip
                  label={openNow ? 'Open now' : 'Closed'}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    bgcolor: openNow
                      ? 'success.light'
                      : 'action.disabledBackground',
                    color: openNow ? 'success.dark' : 'text.secondary',
                  }}
                />
              )}
            </Box>
          )}

          {address && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {address}
            </Typography>
          )}

          {/* Description */}
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            {item.description}
          </Typography>

          {/* Map — interactive, with walking route from user's
              live location. Hidden when destination coords missing. */}
          {(() => {
            const hasCoords =
              typeof item.location?.lat === 'number' &&
              typeof item.location?.lng === 'number' &&
              !(item.location.lat === 0 && item.location.lng === 0);
            if (!hasCoords) return null;
            return (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    color: 'text.secondary',
                    display: 'block',
                    mb: 0.75,
                  }}
                >
                  
                  {t("Where it is")}
                </Typography>
                <MiniMap
                  destination={{
                    lat: item.location.lat,
                    lng: item.location.lng,
                    name: item.title,
                  }}
                  active={true}
                  depth={0}
                  height={220}
                  interactive={true}
                />
              </Box>
            );
          })()}

          {/* Editorial summary from Google when present */}
          {editorial && editorial !== item.description && (
            <Box
              sx={{
                bgcolor: 'action.hover',
                borderRadius: 2,
                p: 1.5,
                mb: 2,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: 'text.secondary',
                  display: 'block',
                  mb: 0.5,
                }}
              >
                
                {t("From Google")}
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                {editorial}
              </Typography>
            </Box>
          )}

          {/* Hours */}
          {hours && hours.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: 'text.secondary',
                  display: 'block',
                  mb: 0.5,
                }}
              >
                
                {t("Hours")}
              </Typography>
              {hours.map((line, i) => (
                <Typography
                  key={i}
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: '0.85rem', lineHeight: 1.6 }}
                >
                  {line}
                </Typography>
              ))}
            </Box>
          )}

          {/* Tags */}
          {item.tags.length > 0 && (
            <Box
              sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2 }}
            >
              {item.tags.map((t) => (
                <Chip
                  key={t}
                  label={t}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.72rem' }}
                />
              ))}
            </Box>
          )}

          {/* Reviews — newest first */}
          {item.place_id && (
            <Box sx={{ mt: 1, mb: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, mb: 1.5 }}
              >
                
                {t("Recent reviews")}
              </Typography>
              {loading && reviews.length === 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    
                    {t("Loading reviews…")}
                  </Typography>
                </Box>
              )}
              {!loading && reviews.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  
                  {t("No recent reviews available.")}
                </Typography>
              )}
              {reviews.map((r, i) => (
                <ReviewItem key={i} review={r} />
              ))}
            </Box>
          )}

          {/* Footer attribution */}
          {item.place_id && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                mt: 1,
                fontSize: '0.7rem',
                opacity: 0.7,
              }}
            >
              
              {t("Info from Google Places")}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Bottom action bar */}
      <Box
        sx={{
          borderTop: 1,
          borderColor: 'divider',
          p: 1.5,
          display: 'flex',
          gap: 1,
          flexShrink: 0,
        }}
      >
        {/* Pin toggle — available for any item with valid coords.
            Pinning surfaces the item on the Map tab. Independent of
            matching: a user can pin without liking, and unpinning
            doesn't unmatch. */}
        {(() => {
          const hasCoords =
            typeof item.location?.lat === 'number' &&
            typeof item.location?.lng === 'number' &&
            !(item.location.lat === 0 && item.location.lng === 0);
          if (!hasCoords) return null;
          return (
            <Button
              variant={isPinned ? 'contained' : 'outlined'}
              color={isPinned ? 'warning' : 'primary'}
              startIcon={isPinned ? <PushPin /> : <PushPinOutlined />}
              onClick={() => {
                if (isPinned) {
                  unpin(item.id);
                } else {
                  pin({
                    id: item.id,
                    title: item.title,
                    description: item.description,
                    image_url: item.image_url ?? null,
                    location: item.location,
                    place_id: item.place_id,
                    chat_prompt: item.chat_prompt,
                    source: 'discover',
                  });
                }
              }}
              sx={{ flexShrink: 0 }}
            >
              {isPinned ? 'Pinned' : 'Pin'}
            </Button>
          );
        })()}
        {mapsUrl && (
          <Button
            fullWidth
            variant="outlined"
            startIcon={<OpenInNew />}
            onClick={() => window.open(mapsUrl, '_blank', 'noopener')}
          >
            {item.origin === 'llm-knowledge' ? 'Find on Maps' : 'Open in Maps'}
          </Button>
        )}
        <Button
          fullWidth
          variant="contained"
          startIcon={<ChatBubbleOutline />}
          onClick={() => {
            onAskAI(item);
            onClose();
          }}
        >
          
          {t("Ask AI")}
        </Button>
      </Box>
    </Drawer>
  );
};

// ────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────

function formatRatingsCount(n: number): string {
  if (n < 1000) return String(n);
  if (n < 10000) return `${(n / 1000).toFixed(1)}k`;
  if (n < 1000000) return `${Math.round(n / 1000)}k`;
  return `${(n / 1000000).toFixed(1)}M`;
}

const StarsRow: React.FC<{ rating: number }> = ({ rating }) => {
  const full = Math.floor(rating);
  const hasHalf = rating - full >= 0.25 && rating - full < 0.75;
  const finalFull = rating - full >= 0.75 ? full + 1 : full;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {Array.from({ length: hasHalf ? full : finalFull }, (_, i) => (
        <Star key={`f${i}`} sx={{ fontSize: 16, color: '#f59e0b' }} />
      ))}
      {hasHalf && <StarHalf sx={{ fontSize: 16, color: '#f59e0b' }} />}
      {Array.from(
        { length: 5 - (hasHalf ? full + 1 : finalFull) },
        (_, i) => (
          <StarBorder key={`e${i}`} sx={{ fontSize: 16, color: '#f59e0b' }} />
        )
      )}
    </Box>
  );
};

const ReviewItem: React.FC<{ review: PlaceReview }> = ({ review }) => {
  const [expanded, setExpanded] = useState(false);
  const isLong = review.text.length > 280;
  const visibleText =
    expanded || !isLong ? review.text : review.text.slice(0, 280) + '…';

  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Avatar
          src={review.author_photo_url}
          sx={{ width: 28, height: 28, fontSize: 14 }}
        >
          {review.author_name.charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, lineHeight: 1.2 }}
          >
            {review.author_name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <StarsRow rating={review.rating} />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: '0.7rem' }}
            >
              {review.relative_time ||
                new Date(review.published_at).toLocaleDateString(undefined, {
                  month: 'short',
                  year: 'numeric',
                })}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Typography variant="body2" sx={{ lineHeight: 1.5, fontSize: '0.9rem' }}>
        {visibleText}
      </Typography>
      {isLong && (
        <Typography
          variant="caption"
          onClick={() => setExpanded((e) => !e)}
          sx={{
            cursor: 'pointer',
            color: 'primary.main',
            fontWeight: 600,
            mt: 0.25,
            display: 'inline-block',
          }}
        >
          {expanded ? 'Show less' : 'Show more'}
        </Typography>
      )}
    </Box>
  );
};
